import * as Dialog from '@radix-ui/react-dialog';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  type Client,
  type ClientCreateInput,
  Client as ClientSchema,
  ClientCreateInput as ClientCreateInputSchema,
} from '@uit/shared/schemas/client';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { apiMutation } from '../../lib/api.js';
import s from './CreateClientDialog.module.scss';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (client: Client) => void;
}

export function CreateClientDialog({ open, onOpenChange, onCreated }: Props) {
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ClientCreateInput>({
    resolver: zodResolver(ClientCreateInputSchema),
    defaultValues: { name: '', legal_name: '', project_or_product_name: '' },
  });

  function close(open: boolean): void {
    onOpenChange(open);
    if (!open) {
      reset();
      setSubmitError(null);
    }
  }

  const onSubmit = async (data: ClientCreateInput): Promise<void> => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const created = await apiMutation<ClientCreateInput, Client>('/api/clients', data, {
        schema: ClientSchema,
      });
      onCreated(created);
      close(false);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      if (message.includes('409')) {
        setSubmitError('A client with that name already exists.');
      } else if (message.includes('400')) {
        setSubmitError('The form has validation errors. Please check the fields above.');
      } else {
        setSubmitError('Could not create client. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={close}>
      <Dialog.Portal>
        <Dialog.Overlay className={s.overlay} />
        <Dialog.Content className={s.content}>
          <Dialog.Title className={s.title}>New client</Dialog.Title>
          <Dialog.Description className={s.description}>
            Required fields are shown below. Per-client contract fields can be added later from the
            client edit page.
          </Dialog.Description>

          <form
            onSubmit={(e) => {
              void handleSubmit(onSubmit)(e);
            }}
            noValidate
          >
            {submitError && (
              <div className={s.formError} role="alert">
                {submitError}
              </div>
            )}

            <div className={s.field}>
              <label className={s.label} htmlFor="client-name">
                Name
              </label>
              <input
                id="client-name"
                type="text"
                className={s.input}
                aria-invalid={!!errors.name}
                {...register('name')}
              />
              {errors.name && <div className={s.fieldError}>{errors.name.message}</div>}
            </div>

            <div className={s.field}>
              <label className={s.label} htmlFor="client-legal-name">
                Legal name
              </label>
              <input
                id="client-legal-name"
                type="text"
                className={s.input}
                aria-invalid={!!errors.legal_name}
                {...register('legal_name')}
              />
              {errors.legal_name && <div className={s.fieldError}>{errors.legal_name.message}</div>}
            </div>

            <div className={s.field}>
              <label className={s.label} htmlFor="client-project">
                Project / product name
              </label>
              <input
                id="client-project"
                type="text"
                className={s.input}
                aria-invalid={!!errors.project_or_product_name}
                {...register('project_or_product_name')}
              />
              {errors.project_or_product_name && (
                <div className={s.fieldError}>{errors.project_or_product_name.message}</div>
              )}
            </div>

            <div className={s.actions}>
              <button
                type="button"
                className={s.cancel}
                onClick={() => {
                  close(false);
                }}
                disabled={submitting}
              >
                Cancel
              </button>
              <button type="submit" className={s.submit} disabled={submitting}>
                {submitting ? 'Creating…' : 'Create client'}
              </button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
